import { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import DownloadIcon from '@mui/icons-material/Download';
import { useAgreementDetail, useAgreementPatch } from '../hooks/useAgreements';
import { useRole, hasMinimumRole } from '../hooks/useRole';
import { FieldEditor } from '../components/agreements/FieldEditor';
import { DiffViewer } from '../components/agreements/DiffViewer';
import { LinkManager } from '../components/agreements/LinkManager';
import { DriverManager } from '../components/agreements/DriverManager';
import { apiDownload } from '../services/api';

const KATEGORI_OPTIONS = [
  { value: 'Kontrakt', label: 'Kontrakt' },
  { value: 'Forskrift', label: 'Forskrift' },
  { value: 'Vedtak', label: 'Vedtak' },
  { value: 'Interkommunalt samarbeid', label: 'Interkommunalt samarbeid' },
  { value: 'Annet', label: 'Annet' },
];

function formatCurrency(v: number | null): string {
  if (v == null) return '\u2014';
  return v.toLocaleString('nb-NO', { maximumFractionDigits: 0 }) + ' kr';
}

function formatDate(v: string | null): string {
  if (!v) return '\u2014';
  return new Date(v).toLocaleDateString('nb-NO');
}

function riskChipColor(value: number | null): 'error' | 'warning' | 'success' | 'default' {
  if (value == null) return 'default';
  if (value >= 0.7) return 'error';
  if (value >= 0.4) return 'warning';
  return 'success';
}

function statusChipColor(status: string): 'success' | 'error' | 'warning' | 'default' {
  if (status === 'success') return 'success';
  if (status === 'failed') return 'error';
  return 'warning';
}

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  if (value !== index) return null;
  return <Box sx={{ p: 3 }}>{children}</Box>;
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

export function AgreementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const role = useRole();
  const canEdit = hasMinimumRole(role, 'Editor');
  const [tabValue, setTabValue] = useState(0);

  const { data, isLoading, error } = useAgreementDetail(id);
  const patchMutation = useAgreementPatch(id || '');

  if (isLoading) {
    return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">Kunne ikke laste avtale: {error.message}</Alert>;
  }

  const a = data?.data;
  if (!a) return null;

  function handleFieldSave(field: string, value: unknown) {
    patchMutation.mutate({ [field]: value });
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/agreements" underline="hover" color="inherit">
          Avtaler
        </Link>
        <Typography color="text.primary">{a.Tittel}</Typography>
      </Breadcrumbs>

      <Typography variant="h2" sx={{ mt: 1 }}>{a.Tittel}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {a.Betaler} &middot; {a.Kategori}
      </Typography>

      {patchMutation.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Feil ved lagring: {patchMutation.error.message}
        </Alert>
      )}

      <Paper variant="outlined">
        <Tabs value={tabValue} onChange={(_e, v) => setTabValue(v)} aria-label="Avtalefaner">
          <Tab label="Oversikt" />
          <Tab label="Felter" />
          <Tab label={`Koblinger (${a.links?.length ?? 0})`} />
          <Tab label="Drivere" />
          <Tab label={`Dokumenter (${a.documents?.length ?? 0})`} />
          <Tab label="Historikk" />
        </Tabs>

        {/* Oversikt */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { lg: '1fr 1fr' }, gap: 4 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Avtaleinformasjon</Typography>
              <InfoRow label="Kategori">{a.Kategori}</InfoRow>
              <InfoRow label="Beløp"><strong>{formatCurrency(a.Beloep_gjeldende)}</strong></InfoRow>
              <InfoRow label="Periode">{formatDate(a.Startdato)} — {a.Sluttdato ? formatDate(a.Sluttdato) : 'Løpende'}</InfoRow>
              <InfoRow label="Mekanikk">{a.Inntektsmekanikk}</InfoRow>
              <InfoRow label="Prislogikk">{a.Prislogikk_type || '\u2014'}</InfoRow>
            </Box>
            <Box>
              {a.Fordeling_json && (() => {
                try {
                  const fordeling = typeof a.Fordeling_json === 'string'
                    ? JSON.parse(a.Fordeling_json)
                    : a.Fordeling_json;
                  if (!Array.isArray(fordeling) || fordeling.length === 0) return null;
                  return (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Fordeling</Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Enhet/avdeling</TableCell>
                            <TableCell align="right">Beløp</TableCell>
                            <TableCell align="right">Andel</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fordeling.map((f: { beskrivelse?: string; beloep?: number; prosent?: number }, i: number) => (
                            <TableRow key={i}>
                              <TableCell>{f.beskrivelse || '\u2014'}</TableCell>
                              <TableCell align="right">{f.beloep != null ? formatCurrency(f.beloep) : '\u2014'}</TableCell>
                              <TableCell align="right">{f.prosent != null ? `${f.prosent} %` : '\u2014'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  );
                } catch { return null; }
              })()}
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Risiko</Typography>
              <InfoRow label="Risiko for bortfall">
                <Chip
                  label={a.Risiko_sannsynlighet != null ? `${(a.Risiko_sannsynlighet * 100).toFixed(0)}%` : '\u2014'}
                  size="small"
                  color={riskChipColor(a.Risiko_sannsynlighet)}
                />
              </InfoRow>
              <InfoRow label="Kommentar">{a.Risiko_kommentar || '\u2014'}</InfoRow>
              {a.Beskrivelse && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Beskrivelse</Typography>
                  <Typography variant="body2" color="text.secondary">{a.Beskrivelse}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </TabPanel>

        {/* Felter — inline redigering */}
        <TabPanel value={tabValue} index={1}>
          <dl>
            <FieldEditor label="Tittel" field="Tittel" value={a.Tittel} canEdit={canEdit} onSave={handleFieldSave} isSaving={patchMutation.isPending} />
            <FieldEditor label="Betaler" field="Betaler" value={a.Betaler} canEdit={canEdit} onSave={handleFieldSave} isSaving={patchMutation.isPending} />
            <FieldEditor label="Beskrivelse" field="Beskrivelse" value={a.Beskrivelse} canEdit={canEdit} onSave={handleFieldSave} isSaving={patchMutation.isPending} />
            <FieldEditor label="Kategori" field="Kategori" value={a.Kategori} type="select" options={KATEGORI_OPTIONS} canEdit={canEdit} onSave={handleFieldSave} isSaving={patchMutation.isPending} />
            <FieldEditor label="Startdato" field="Startdato" value={a.Startdato} type="date" canEdit={canEdit} onSave={handleFieldSave} isSaving={patchMutation.isPending} />
            <FieldEditor label="Sluttdato" field="Sluttdato" value={a.Sluttdato} type="date" canEdit={canEdit} onSave={handleFieldSave} isSaving={patchMutation.isPending} />
            <FieldEditor label="Gjeldende beløp" field="Beloep_gjeldende" value={a.Beloep_gjeldende} type="number" canEdit={canEdit} onSave={handleFieldSave} isSaving={patchMutation.isPending} />
            <FieldEditor label="Prislogikk" field="Prislogikk_type" value={a.Prislogikk_type} canEdit={canEdit} onSave={handleFieldSave} isSaving={patchMutation.isPending} />
            <FieldEditor label="Inntektsmekanikk" field="Inntektsmekanikk" value={a.Inntektsmekanikk} canEdit={canEdit} onSave={handleFieldSave} isSaving={patchMutation.isPending} />
            <FieldEditor label="Risiko for bortfall" field="Risiko_sannsynlighet" value={a.Risiko_sannsynlighet} type="number" canEdit={canEdit} onSave={handleFieldSave} isSaving={patchMutation.isPending} />
            <FieldEditor label="Risiko kommentar" field="Risiko_kommentar" value={a.Risiko_kommentar} canEdit={canEdit} onSave={handleFieldSave} isSaving={patchMutation.isPending} />
            <FieldEditor label="Aktiv" field="Er_aktiv" value={a.Er_aktiv} type="boolean" canEdit={canEdit} onSave={handleFieldSave} isSaving={patchMutation.isPending} />
          </dl>
        </TabPanel>

        {/* Koblinger */}
        <TabPanel value={tabValue} index={2}>
          <LinkManager
            avtaleId={id!}
            canEdit={canEdit}
            beloepGjeldende={a.Beloep_gjeldende}
            fordelingJson={a.Fordeling_json}
          />
        </TabPanel>

        {/* Drivere */}
        <TabPanel value={tabValue} index={3}>
          <DriverManager avtaleId={id!} canEdit={canEdit} beloepGjeldende={a.Beloep_gjeldende} />
        </TabPanel>

        {/* Dokumenter */}
        <TabPanel value={tabValue} index={4}>
          {a.documents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Ingen dokumenter knyttet til denne avtalen.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Filnavn</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Lastet opp</TableCell>
                  <TableCell>Av</TableCell>
                  <TableCell align="center">Last ned</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {a.documents.map((doc) => (
                  <TableRow key={doc.DokumentID}>
                    <TableCell>
                      <Link
                        component="button"
                        underline="hover"
                        onClick={() => apiDownload(`/documents/${doc.DokumentID}/content`, doc.Filnavn_original)}
                      >
                        {doc.Filnavn_original}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Chip label={doc.Analyse_status} size="small" color={statusChipColor(doc.Analyse_status)} />
                    </TableCell>
                    <TableCell>{new Date(doc.Lastet_opp_dato).toLocaleDateString('nb-NO')}</TableCell>
                    <TableCell>{doc.Lastet_opp_av || '\u2014'}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => apiDownload(`/documents/${doc.DokumentID}/content`, doc.Filnavn_original)}
                        title="Last ned dokument"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabPanel>

        {/* Historikk */}
        <TabPanel value={tabValue} index={5}>
          <DiffViewer entries={a.auditHistory ?? []} />
        </TabPanel>
      </Paper>
    </Box>
  );
}
